import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';

@Component({
  selector: 'kl-textarea',
  standalone: false,
  templateUrl: './kl-textarea.html',
  styleUrl: './kl-textarea.scss',
})
export class KlTextareaComponent implements OnInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  readonly formGroup = input.required<FormGroup>();
  readonly controlName = input.required<string>();

  readonly label = input('');
  readonly labelDescription = input('');
  readonly placeholder = input('');
  readonly rows = input(4);
  readonly autoResize = input(false);

  readonly minlength = input<number | null>(null);
  readonly maxlength = input<number | null>(null);

  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginY = input(false);
  readonly marginBottom = input(true);

  /** When true, show validation error text under the control. */
  readonly showFieldError = input(false);

  readonly inputId = input('');

  readonly valueChange = output<unknown>();

  readonly control = computed<AbstractControl | null>(() => this.formGroup()?.get(this.controlName()) ?? null);

  /**
   * `control.invalid`/`.touched`/`.errors` are plain mutable state, not signals, so a `computed()`
   * reading them never re-runs on its own — e.g. `form.markAllAsTouched()` from a save handler
   * wouldn't turn on this field's red border. Bump this on every control event so the
   * validation-derived computeds below stay live.
   */
  private readonly controlState = signal(0);
  private readonly controlStateSync = effect((onCleanup) => {
    const sub = this.control()?.events.subscribe(() => this.controlState.update((v) => v + 1));
    onCleanup(() => sub?.unsubscribe());
  });

  readonly fieldId = computed(() => (this.inputId() || this.controlName() || 'kl-textarea').replace(/\s/g, '_'));

  readonly required = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.validator) return false;
    const v = c.validator({} as AbstractControl);
    return !!v?.['required'];
  });

  readonly showInvalid = computed(() => {
    this.controlState();
    const c = this.control();
    return !!(c && c.invalid && c.touched);
  });

  readonly controlError = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.invalid || !c.touched) return null;
    if (c.errors?.['required']) return 'This field is required.';
    if (c.errors?.['minlength']) {
      const len = (c.errors['minlength'] as { requiredLength: number }).requiredLength;
      return `Minimum length is ${len}.`;
    }
    if (c.errors?.['maxlength']) {
      const len = (c.errors['maxlength'] as { requiredLength: number }).requiredLength;
      return `Maximum length is ${len}.`;
    }
    return null;
  });

  ngOnInit(): void {
    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);
  }

  onAnyChange(): void {
    this.valueChange.emit(this.control()?.value);
  }
}
